// projects/huda/src/app/core/services/firebase-init.service.ts
import { Injectable, signal, WritableSignal } from '@angular/core';
import { FirebaseApp, initializeApp } from 'firebase/app';
import { Auth, getAuth, onAuthStateChanged, signInWithCustomToken } from 'firebase/auth'; // Removed signInAnonymously
import { doc, Firestore, getDoc, getFirestore } from 'firebase/firestore'; // Added doc, getDoc
import { FirebaseStorage, getStorage } from 'firebase/storage';
import { environment } from '../../../environments/environment';
import { FIRESTORE_PATHS } from '../models/path.model';
import { User } from '../models/user.model';
import { Functions, getFunctions } from 'firebase/functions';

declare const __app_id: string; // Keep this for Canvas environment's app ID
declare const __initial_auth_token: string | undefined; // Keep this for Canvas environment's auth token

@Injectable({
  providedIn: 'root',
})
export class FirebaseInitService {
  private _db: Firestore | undefined;
  private _auth: Auth | undefined;
  private _storage: FirebaseStorage | undefined;
  private _appId: string;
  private _app: FirebaseApp | undefined;
  private _functions: Functions | undefined;

  public firebaseUser: WritableSignal<User | null> = signal(null);
  // isAuthReady now indicates if both Auth state and approval check are complete
  public isAuthReady: WritableSignal<boolean> = signal(false);
  // isUserApproved: null = not checked/not logged in, true = approved, false = not approved
  public isUserApproved: WritableSignal<boolean | null> = signal(null);
  public userId: WritableSignal<string | null> = signal(null); // Explicit userId signal

  constructor() {
    this._appId = typeof __app_id !== 'undefined' ? __app_id : 'default-app-id';
    this.initializeFirebase();
  }

  private async initializeFirebase(): Promise<void> {
    try {
      const firebaseConfig = environment.firebaseConfig;

      if (!firebaseConfig || Object.keys(firebaseConfig).length === 0) {
        console.error('Firebase config is empty in environment.ts. Cannot initialize Firebase.');
        // Set authReady to true but user to null if config is bad, to unblock app.
        this.isAuthReady.set(true);
        this.firebaseUser.set(null);
        this.userId.set(null);
        this.isUserApproved.set(false); // Can't be approved without config
        return;
      }

      this._app = initializeApp(firebaseConfig);
      this._db = getFirestore(this._app);
      this._auth = getAuth(this._app);
      this._storage = getStorage(this._app);
      this._functions = getFunctions(this._app, 'us-central1'); // Specify region to match Cloud Functions

      onAuthStateChanged(this._auth, async (user) => {
        // this.firebaseUser.set(user); // Update the user signal immediately
        this.userId.set(user ? user.uid : null); // Update userId signal

        if (user) {
          console.log('Firebase Auth State Changed: User logged in:', user.uid);
          // User is logged in, now check approval status
          await this.checkUserApproval(user.uid);
        } else {
          // User is not logged in or logged out
          console.log('Firebase Auth State Changed: User not logged in.');
          // Attempt to sign in with custom token ONLY if available
          if (typeof __initial_auth_token !== 'undefined' && __initial_auth_token) {
            try {
              // Sign in with custom token, then onAuthStateChanged will be called again
              // with the new user object if successful.
              await signInWithCustomToken(this._auth!, __initial_auth_token);
              console.log('Attempting sign in with custom token...');
              // Do NOT set isAuthReady or isUserApproved here.
              // The next onAuthStateChanged call will handle it.
              return; // Exit to avoid double-setting isAuthReady
            } catch (error) {
              console.error('Error signing in with custom token. User remains unauthenticated:', error);
              // If custom token fails, proceed as unauthenticated
              this.isUserApproved.set(false); // Not approved
            }
          } else {
            // No custom token, user is genuinely unauthenticated
            this.isUserApproved.set(false); // Not approved
          }
          // After all checks, if user is still null, mark auth ready
          this.isAuthReady.set(true);
        }
      });
    } catch (error) {
      console.error('Error initializing Firebase:', error);
      this.isAuthReady.set(true);
      this.firebaseUser.set(null);
      this.userId.set(null);
      this.isUserApproved.set(false);
    }
  }

  // New method to check user approval status from Firestore
  private async checkUserApproval(uid: string): Promise<void> {
    if (!this._db) {
      console.error('Firestore not initialized, cannot check user approval.');
      this.isUserApproved.set(false); // Assume not approved if DB is down
      this.isAuthReady.set(true);
      return;
    }

    try {
      // Assuming 'approved' property is directly on the user's document
      // at path /users/{userId}
      const userDocRef = doc(this._db, FIRESTORE_PATHS.USERS, uid);
      const userDocSnap = await getDoc(userDocRef);

      if (userDocSnap.exists()) {
        const userData = userDocSnap.data();
        this.firebaseUser.set(userData as User); // Set the user signal with the Firebase user object
        const approvedStatus = !!userData?.['approved']; // Convert to boolean, default to false if undefined/null
        this.isUserApproved.set(approvedStatus);
        console.log(`User ${uid} approval status: ${approvedStatus}`);
      } else {
        // User document does not exist, consider them not approved
        this.isUserApproved.set(false);
        console.warn(`User document for ${uid} not found. Considering not approved.`);
      }
    } catch (error) {
      console.error('Error fetching user approval status:', error);
      this.isUserApproved.set(false); // On error, default to not approved for security
    } finally {
      this.isAuthReady.set(true); // Mark auth process as complete regardless of approval status
    }
  }

  get db(): Firestore {
    if (!this._db) {
      throw new Error('Firestore not initialized. Ensure FirebaseInitService is used after initialization.');
    }
    return this._db;
  }

  get auth(): Auth {
    if (!this._auth) {
      throw new Error('Auth not initialized. Ensure FirebaseInitService is used after initialization.');
    }
    return this._auth;
  }

  get storage(): FirebaseStorage {
    if (!this._storage) {
      throw new Error('Storage not initialized. Ensure FirebaseInitService is used after initialization.');
    }
    return this._storage;
  }

  get app(): FirebaseApp {
    if (!this._app) {
      throw new Error('Firebase app not initialized. Ensure FirebaseInitService is used after initialization.');
    }
    return this._app;
  }

  get functions(): Functions {
    if (!this._functions) {
      throw new Error('Functions not initialized. Ensure FirebaseInitService is used after initialization.');
    }
    return this._functions;
  }

  get appId(): string {
    return this._appId;
  }
}
